import React, { useState, useEffect } from 'react';
import { Card, Image, Row, Col, Button, Typography, Space, message, Popconfirm } from 'antd';
import { DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface Photo {
  _id: string;
  contentType: string;
  createdAt: string;
}

const PhotoGallery: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://eggs.aeve.ru/api/photos');
      if (response.ok) {
        const data = await response.json();
        setPhotos(data);
      } else {
        throw new Error('Ошибка загрузки фотографий');
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
      message.error('Ошибка при загрузке фотографий');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const deletePhoto = async (id: string) => {
    try {
      const response = await fetch(`https://eggs.aeve.ru/api/photos/${id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        message.success('Фото удалено');
        fetchPhotos(); // Обновляем список
      } else {
        throw new Error('Ошибка удаления');
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      message.error('Ошибка при удалении фото');
    }
  };

  const downloadPhoto = (id: string) => {
    const link = document.createElement('a');
    link.href = `https://eggs.aeve.ru/api/photos/${id}`;
    link.download = `photo_${id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="gallery-container">
      <Card 
        title={
          <Space>
            <EyeOutlined />
            <span>Галерея фотографий</span>
          </Space>
        }
        extra={
          <Button 
            type="primary" 
            onClick={fetchPhotos}
            loading={loading}
          >
            Обновить
          </Button>
        }
      >
        {photos.length === 0 ? (
          <div className="empty-gallery">
            <Text type="secondary">Пока нет сохраненных фотографий</Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {photos.map((photo) => (
              <Col xs={24} sm={12} md={8} lg={6} key={photo._id}>
                <Card
                  hoverable
                  cover={
                    <Image
                      src={`https://eggs.aeve.ru/api/photos/${photo._id}`}
                      alt="Фото"
                      style={{ height: 200, objectFit: 'cover' }}
                      preview={{
                        mask: 'Нажмите для увеличения'
                      }}
                    />
                  }
                  actions={[
                    <Button 
                      type="text" 
                      icon={<DownloadOutlined />}
                      onClick={() => downloadPhoto(photo._id)}
                    >
                      Скачать
                    </Button>,
                    <Popconfirm
                      title="Удалить фото?"
                      description="Это действие нельзя отменить"
                      onConfirm={() => deletePhoto(photo._id)}
                      okText="Да"
                      cancelText="Нет"
                    >
                      <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />}
                      >
                        Удалить
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={`Фото ${photo._id.slice(-6)}`}
                    description={new Date(photo.createdAt).toLocaleString('ru-RU')}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
};

export default PhotoGallery;
